import React, { Fragment, useEffect, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// MUI
import Collapse from "@mui/material/Collapse";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";

// MUI Icons
import AddCommentIcon from "@mui/icons-material/AddComment";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import InfoIcon from "@mui/icons-material/Info";
import CommentIcon from "@mui/icons-material/Comment";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ApartmentIcon from '@mui/icons-material/Apartment';
import ShareIcon from '@mui/icons-material/Share';

// Custom Icons
import POIIcon from "@/icons/POIIcon";
import POIIconKuca from "@/icons/POIIconKuca";
import POIIconVrh from "@/icons/POIIconVrh";
import StazaIcon from "@/icons/StazaIcon";
import ZemljovidIcon from "@/icons/ZemljovidIcon";
import Photo360Icon from "@/icons/Photo360Icon";
import ZasticenoPodrucjeIcon from "@/icons/ZasticenoPodrucjeIcon";

// Custom Components
import MapCard from "@/ui/MapCard/MapCard";
import MapCardHeader from "@/ui/MapCard/MapCardHeader";
import MapCardAvatar from "@/ui/MapCard/MapCardAvatar";
import MapCardContent from "@/ui/MapCard/MapCardContent";
import MapCardActions from "@/ui/MapCard/MapCardActions";
import MapCardActionButton from "@/ui/MapCard/MapCardActionButton";
import dataController from "@/lib/dataController";
import { sifrarnici } from "@/lib/sifrarnici";
import MapCardHeaderButtonClose from "@/ui/MapCard/MapCardHeaderButtonClose";
import DialogContext from "@/ui/DialogContext/DialogContext";
import { GridContainer, GridItem } from "@/ui/Grid";
import ModelTable from "@/ui/Table/ModelTable";
import ShareLink from "@/ui/ShareLink/ShareLink";

import FormContent from "@/components/FormContent";
import UserContext from "@/components/UserContext/UserContext";

import modelPOI from "@/models/poi";
import modelZemljovidi from "@/models/zemljovidi";
import modelStaze from "@/models/staze";
import modelKomentari from "@/models/komentari";
import modelPhoto360 from "@/models/photos360";
import modelZP from "@/models/zp";
import modelDKP from "@/models/dkp";
import modelPosjednici from "@/models/posjednici";
import modelVlasnici from "@/models/zk_vlasnici";

import POIDialog from "@/views/GIS/POIPage/POIDialog";
import ZemljovidDialog from "@/views/ZemljovidiPage/ZemljovidDialog";
import StazaDialog from "@/views/GIS/StazePage/StazaDialog";
import Image360 from "@/views/GIS/Image360";
import AllObjectCommentsTable from "@/views/GIS/MapPage/AllObjectCommentsTable";
// import InspectionDialog from "Views/MapPage/InspectionDialog";

// Types
import { GSInfoCardProps } from "@/@types/views/GIS/map";
import { IModel } from "@/@types/models/model";
import { Permissions } from "@/lib/permissions";
import { OwnerOrganisations } from "@/lib/ownerOrganisations";
import { DCRecord } from "@/@types/lib/dataController";
import CommentDialog from "@/views/Komentari/CommentDialog";

import MapContext from "@/components/Map/MapContext";
import api from "@/lib/api";
import { Feature } from "ol";
import { LineString, Point, Polygon } from "ol/geom";

import { prepareCoordinatesForDisplay } from "@/lib/olHelpers";

function GSInfoCard(props: GSInfoCardProps) {
  const { t } = useTranslation();

  const initialRecod = props.featureData.record
    ? props.featureData.record
    : props.featureData.feature.properties;

  const [record, setRecord] = useState(initialRecod);
  const [showAllComments, setShowAllComments] = useState(false);
  const [showShareLink, setShowShareLink] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const dialogContext = useContext(DialogContext);
  const userContext = useContext(UserContext);
  const mapContext = useContext(MapContext);
  const navigate = useNavigate();

  const {
    featureData,
    onClose,
    expandedPosjednici = false,
    setExpandedPosjednici,
    expandedVlasnici = false,
    setExpandedVlasnici,
    updateCardOverlay,
    refreshLayers,
    commentRecords,
    onAddToRoute
  } = props;


  const [recordsPosjednici, setRecordsPosjednici] = useState<DCRecord[]>([]);
  const [recordsVlasnici, setRecordsVlasnici] = useState<DCRecord[]>([]);

  const getModel = (type?: string) => {
    if (!type) {
      return null;
    }
    if (type.slice(0, 4) === "poi_") {
      return modelPOI;
    } else if (
      type.toLowerCase().indexOf("staza") !== -1 ||
      type.toLowerCase().indexOf("staze") !== -1
    ) {
      return modelStaze;
    } else if (type === "zemljovidi") {
      return modelZemljovidi;
    } else if (type === "photos360") {
      return modelPhoto360;
    } else if (type === "zasticeno_podrucje_poligoni") {
      return modelZP;
    } else if (type === "DKP") {
      return modelDKP;
    } else {
      return null;
    }
  };

  const definePOIForm = (record: DCRecord) => {

    let recordOwnerId = record.owner_organisation_id as number; // 20 za hgss, 30 za hps
    if(recordOwnerId === undefined) {
      recordOwnerId = record.owner_organisation as number; // 20 za hgss, 30 za hps
    }

    if (userContext?.hasAnyPermission([Permissions.HGSSMod, Permissions.HGSSRead])) {
      if (recordOwnerId === OwnerOrganisations.HGSS.code) {
        return "hgss_user_on_hgss_data"
      }
      else if (recordOwnerId === OwnerOrganisations.HPS.code) {
        return "hgss_user_on_hps_data"
      }
      else {
        console.warn("how did u get here? 1")
        return "edit"
      }
    }
    else if (userContext?.hasAnyPermission([Permissions.HPSMod, Permissions.HPSRead])) {
      if (recordOwnerId === OwnerOrganisations.HGSS.code) {
        return "hps_user_on_hgss_data"
      }
      else if (recordOwnerId === OwnerOrganisations.HPS.code) {
        return "hps_user_on_hps_data"
      }
      else {
        console.warn("how did u get here? 2")
        return "edit"
      }
    }
    else {
      console.log("how did u get here? 3")
      return "edit"
    }
  };

  const getDialog = (model: IModel) => {
    switch (model.apiPath) {
      case "poi":
        return POIDialog;
      case "staze":
        return StazaDialog;
      case "zemljovidi":
        return ZemljovidDialog;
      default:
        return null;
    }
  };

  const getIcon = (model: IModel, record: DCRecord) => {
    switch (model.apiPath) {
      case "poi":
        switch (record.poi_tip_id) {
          case sifrarnici.poi_tipovi.planinarska_kuca:
          case sifrarnici.poi_tipovi.planinarski_dom:
          case sifrarnici.poi_tipovi.planinarsko_skloniste:
            return <POIIconKuca />;
          case sifrarnici.poi_tipovi.vrh:
            return <POIIconVrh />
          default:
            return <POIIcon />
        }
      case "staze":
        return <StazaIcon />;
      case "zemljovidi":
        return <ZemljovidIcon />;
      case "photos360":
        return <Photo360Icon />;
      case "zp":
        return <ZasticenoPodrucjeIcon />;
      case "dkp":
        return <ApartmentIcon />
      default:
        return <i className={faIconClass} color="red"></i>;
    }
  };

  const getTitle = (model: IModel, record: DCRecord) => {
    switch (model.apiPath) {
      case "poi":
        return record.vrsta as string;
      case "staze":
        return record.vrsta ? (record.vrsta as string) : "Staza";
      case "zemljovidi":
        return t("titles.zemljovidi");
      case "photos360":
        return t("titles.photo360");
      case "zp":
        return t("titles.zasticeno_podrucje");
      case "dkp":
        return t("titles.dkp");
      default:
        return "Info";
    }
  };

  const modelType = getModel(featureData.layerId);
  if (modelType === null) {
    throw new Error(
      "Unrecognized type passed to GSInfoCard: " + featureData.layerId
    );
  }
  const dc = new dataController(modelType);
  const dcPosjednici = new dataController(modelPosjednici);
  const dcVlasnici = new dataController(modelVlasnici);

  const faIconClass = "fas fa-info ";

  const recordId = record ? record.id : null;

  const handleEdit = () => {
    const Dialog = getDialog(modelType);
    if (Dialog === null) {
      throw new Error(
        "Unrecognized type passed to GSInfoCard: " + featureData.layerId
      );
    }

    const form = definePOIForm(record);

    if (modelType.apiPath === 'poi') {
      navigate('/hgss/poi/' + String(record.id), { state: { view: 'update', form: form } });
    } else {
      dialogContext.showDialog(Dialog, {
        dc: dc,
        recordId: record.id,
        mode: "update",
        form: form,
        iconClass: faIconClass,
        onClose: handleDataDialogClose,
        preClose: updateCardOverlay,
      });
    }    
  };

  const handleRedirect = (url: string) => {
    window.open(url);
  };

  const handleDataDialogClose = (dialogResult: {
    dataChanged: boolean;
    action: string;
  }) => {
    if (dialogResult.dataChanged) {
      if (refreshLayers) refreshLayers();
    }
  };

  const handleAddComment = () => {
    const dcKomentari = new dataController(modelKomentari);

    // quantize coordinates of comment to the POI instead of mouse position
    const coords = (featureData.feature instanceof Feature) ? featureData.feature.getGeometry().getCoordinates() : featureData.feature.geometry.coordinates;
    // const coords = proj4("EPSG:3857", "EPSG:3765", featureData.position);
    // console.log(coords);

    // build WKT
    const wkt = "POINT(" + featureData.position[0] + " " + featureData.position[1] + ")";
    

    dialogContext.showDialog(CommentDialog, {
      dc: dcKomentari,
      mode: "insert",
      form: "insert",
      iconClass: faIconClass,
      onClose: handleDataDialogClose,
      refreshLayers: refreshLayers,
      initialRecord: {
        objekt_id: record.id, // stavi u record.broj_staze ako zelis spremat broj staze a ne id u tablici
        objekt_type: modelType.apiPath,
        x: parseInt(coords[0].toString(), 10),
        y: parseInt(coords[1].toString(), 10),
        wkt: wkt,
      },
    });
  };

  const handleShowAllCommnetsclick = () => {
    setShowAllComments(!showAllComments);
  };


  const loadPosjedniciDKP = () => {
    if (record) {
     dcPosjednici.GetData(`dkp/${record.id}/posjednici/`).then((resp) => {
        if (resp.success && Array.isArray(resp.data)) {
          setRecordsPosjednici(resp.data);
        }
      });
    }
  };

  const loadVlasniciDKP = () => {
    if (record) {
      dcVlasnici.GetData(`dkp/${record.id}/zk-vlasnici/`).then((resp) => {
        if (resp.success && Array.isArray(resp.data)) {
          setRecordsVlasnici(resp.data);
        }
      });
    }
  };

  const handleShare = () => {
    const apiInstance = new api();

    if (!mapContext || !mapContext.map) return;
    if (showShareLink) {
      setShowShareLink(!showShareLink);
      return;
    }

    const position = featureData.position;
    const geom = featureData.feature.geometry;

    let feature;
    let object_type;
    if (featureData.feature instanceof Feature) {
      feature = featureData.feature;
      switch (feature?.getGeometry()?.getType()) {
        case "Point": {
          object_type = "poi";
          break;
        }
        case "LineString": {
          object_type = "staza";
          break;
        }
        default:
          object_type = "poi";
      }
    } else {
      switch (geom.type) {
        case "Point": {
          feature = new Feature({
            geometry: new Point(geom.coordinates),
          });
          object_type = "poi";
          break;
        }
        case "LineString": {
          feature = new Feature({
            geometry: new LineString(geom.coordinates),
          });
          object_type = "staza";
          break;
        }
      }
    }
    if (feature) {
      const extent = feature.getGeometry()?.getExtent();

      const maplayers = localStorage.getItem("maplayers");

      const config = {
        position: position,
        extent: extent,
        maplayers: maplayers,
      };
      const object_id = featureData?.record?.id;

      const data = {
        config: JSON.stringify(config),
        object_id: object_id,
        object_type: object_type,
      };

      apiInstance.Call("share-generate", "post", data).then((resp) => {
        // @ts-ignore
        if (resp.success && resp?.data?.url_hash) {
          setShareUrl(
            // @ts-ignore
            `${window.location.origin}/share?hash=${resp.data.url_hash}`
          );
          setShowShareLink(!showShareLink);
        }
      });
    }
  };

  const handleTogglePosjednici = () => {
    const newValue = !expandedPosjednici;
    if (setExpandedPosjednici) {
      setExpandedPosjednici(newValue);
      if (setExpandedVlasnici && newValue) {
        setExpandedVlasnici(false);
      }
    }
  };

  const handleToggleVlasnici = () => {
    const newValue = !expandedVlasnici;
    if (setExpandedVlasnici) {
      setExpandedVlasnici(newValue);
      if (setExpandedPosjednici && newValue) {
        setExpandedPosjednici(false);
      }
    }
  };

    useEffect(() => {
    if (recordId) {
      if (modelType.apiPath === "dkp") {
        loadPosjedniciDKP();
        loadVlasniciDKP();
      }    
    }
  }, [recordId]);

  useEffect(() => {
    if (featureData) {
      if (featureData.record) {
        setRecord(featureData.record);
      } else if (featureData.feature) {
        setRecord(featureData.feature.properties);
      }
    }
    setRecord;
  }, [featureData]);

  // const title = record.vrsta ? (record.vrsta === 5 ? "Planinarska staza" : "Ostalo") : t("titles.zemljovidi");
  const title = getTitle(modelType, record);
  const icon = getIcon(modelType, record);

  const hasEditButton =
    ["poi", "staze"].indexOf(modelType.apiPath) >= 0 || ((["zemljovidi"].indexOf(modelType.apiPath) >= 0) && userContext?.hasAnyPermission([Permissions.HGSSMod])) ? true : false;
  const hasAllCommentsbutton =
    ["poi", "staze"].indexOf(modelType.apiPath) >= 0 ? true : false;
  const hasShareButton =
    ["poi", "staze"].indexOf(modelType.apiPath) >= 0 ? true : false;
  const hasVlasniciButton = modelType.apiPath === "dkp" ? true : false;
  const hasPosjedniciButton = modelType.apiPath === "dkp" ? true : false;
  const isPhoto360 = featureData.layerId === "photos360" ? true : false;

  const handleAddToRoute = () => {
    if (onAddToRoute) {
      const coordinates = featureData.position;
      const displayCoordinates = prepareCoordinatesForDisplay(coordinates);
      const dmsCoordinates = `${displayCoordinates[1]}N ${displayCoordinates[0]}E`;
      const f = featureData.feature;
      const naziv = f.getProperties ? f.getProperties().naziv : f.properties ? f.properties.naziv : null;
      if (featureData.feature) {
        onAddToRoute(coordinates, naziv || dmsCoordinates);
      }
    }
   }

  return (
    <MapCard
      sx={{
        backgroundColor: "background.default",
        minWidth: "400px",
      }}
    >
      <MapCardHeader
        title={title}
        subheader={`${t("common.id")}: ${record.id}`}
        subheaderTypographyProps={{ color: "initial" }}
        avatar={<MapCardAvatar ariaLabel="info" icon={icon} />}
        action={
          <MapCardHeaderButtonClose
            onClick={() => {
              onClose();
              setShowAllComments(false);
            }}
          />
        }
      />
      <MapCardContent>

        {isPhoto360 ? (
          // @ts-ignore
          <Image360 fileName={featureData.feature.properties.filename} />
        ) : (
          <GridContainer spacing={0}>
            <FormContent
              fields={dc.getFormFields("overlay")}
              fieldNames={dc.getFormFieldsNames("overlay")}
              columns={1}
              mode="simpleView"
              record={record}
            />
          </GridContainer>
        )}
      </MapCardContent>
      <MapCardActions>
        {(modelType.apiPath === "staze" || modelType.apiPath === "poi") &&
        commentRecords &&
        commentRecords.length === 0 ? (
          <MapCardActionButton
            startIcon={<AddCommentIcon />}
            variant="contained"
            onClick={() => handleAddComment()}
          >
            {t("buttons.add_comment")}
          </MapCardActionButton>
        ) : null}
        {hasEditButton ? (
          <MapCardActionButton
            startIcon={<EditIcon />}
            variant="contained"
            onClick={() => handleEdit()}
            needPermission={[Permissions.HGSSMod, Permissions.HPSMod]}
          >
            {t("buttons.edit")}
          </MapCardActionButton>
        ) : null}
        {record && record.info_link ? (
          <MapCardActionButton
            startIcon={<InfoIcon />}
            variant="contained"
            onClick={() => handleRedirect(record.info_link as string)}
          >
            {t("buttons.more_info")}
          </MapCardActionButton>
        ) : null}
        {hasAllCommentsbutton &&
        commentRecords &&
        commentRecords.length > 0 ? (
          <MapCardActionButton
            startIcon={<CommentIcon />}
            variant="contained"
            onClick={() => handleShowAllCommnetsclick()}
            needPermission={[Permissions.HGSSRead, Permissions.HPSRead]}
          >
            {t("buttons.show_all_object_comments")}
          </MapCardActionButton>
        ) : null}
        {hasShareButton ? (
          <MapCardActionButton
            startIcon={<ShareIcon />}
            variant="contained"
            onClick={() => handleShare()}
          >
            {t("buttons.share")}
          </MapCardActionButton>
        ) : null}
        {hasPosjedniciButton ? (
          <MapCardActionButton
            startIcon={<AssignmentIndIcon />}
            endIcon={
              <ExpandMoreIcon
                sx={[
                  {
                    transform: 'rotate(0deg)',
                    transition: (theme) => theme.transitions.create('all', {
                      duration: theme.transitions.duration.shortest,
                    })
                  },
                  expandedPosjednici && {
                    transform: 'rotate(180deg)'
                  }
                ]}
              />
            }
            onClick={handleTogglePosjednici}
            aria-expanded={expandedPosjednici}
            aria-label="expand"
            disabled={recordsPosjednici.length === 0}
          >
            {t("buttons.posjednici")}
          </MapCardActionButton>
        ) : null}

        {hasVlasniciButton ? (
          <MapCardActionButton
            startIcon={<AssignmentIndIcon />}
            endIcon={
              <ExpandMoreIcon
                sx={[
                  {
                    transform: 'rotate(0deg)',
                    transition: (theme) => theme.transitions.create('all', {
                      duration: theme.transitions.duration.shortest,
                    })
                  },
                  expandedVlasnici && {
                    transform: 'rotate(180deg)'
                  }
                ]}
              />
            }
            onClick={handleToggleVlasnici}
            aria-expanded={expandedVlasnici}
            aria-label="expand"
            disabled={recordsVlasnici.length === 0}
          >
            {t("buttons.vlasnici")}
          </MapCardActionButton>
        ) : null}
        {onAddToRoute ? 
          <Tooltip title={t("route.buttons.add_point")}>
            <IconButton size="small"
              onClick={handleAddToRoute}
            >
              <AddCircleIcon fontSize="large" color="primary" />
            </IconButton>
          </Tooltip>
        : null}
      </MapCardActions>
      {showShareLink ? <ShareLink value={shareUrl} /> : null}
      {
        showAllComments && commentRecords && commentRecords.length>0 && 
            <Box bgcolor={"white"} border={"1px solid #adadad"} > 
              <AllObjectCommentsTable records={commentRecords} handleAddComment={handleAddComment}></AllObjectCommentsTable>
            </Box>
          }
              {hasPosjedniciButton || hasVlasniciButton ? (
      <Collapse in={expandedPosjednici || expandedVlasnici } timeout="auto" unmountOnExit sx={{ maxHeight: 400, overflowX: 'scroll' }}>
        { expandedPosjednici ? 
        <ModelTable 
          title=" " 
          tableName="posjednici"
          viewName="default"
          records={recordsPosjednici || []} 
          dc={dcPosjednici} 
          allowSelection="none" 
          allowAdd={false}
          allowRowAction={(() => { return false; })}
          onRowAction={() => {}} 
          /> : null }
        { expandedVlasnici ?
        <ModelTable 
          title=" " 
          tableName="vlasnici"
          viewName="default"
          records={recordsVlasnici || []} 
          dc={dcVlasnici} 
          allowSelection="none" 
          allowAdd={false}
          allowRowAction={(() => { return false; })}
          onRowAction={() => {}} 
          />
        : null }
      </Collapse>
    ) : null}    
    </MapCard>
  );
}

export default GSInfoCard;
